import { useMemo, useState } from 'react';
import { cn } from '../../../utils/cn';

/** Pure: stack values per point in key order. */
export function stackSeries(points, keys) {
	const layers = Object.fromEntries(keys.map((k) => [k.key, []]));
	let max = 0;
	points.forEach((p, i) => {
		let acc = 0;
		for (const k of keys) {
			const v = Math.max(0, Number(p.values?.[k.key]) || 0);
			layers[k.key][i] = { y0: acc, y1: acc + v };
			acc += v;
		}
		if (acc > max) max = acc;
	});
	return { layers, max };
}

/**
 * Stacked-area series in inline SVG. Colors come from `keys[].color`
 * (tokens resolved by the caller), never hardcoded here.
 */
export function SeriesChart({ points = [], keys = [], height = 180, ariaLabel = 'Series over time', emptyText = 'No data yet', onHover, className }) {
	const W = 800, H = height, padX = 8, padTop = 10, padBottom = 18;
	const [hoverIdx, setHoverIdx] = useState(null);
	const { layers, max } = useMemo(() => stackSeries(points, keys), [points, keys]);

	if (points.length === 0) {
		return <div className={cn('flex items-center justify-center rounded-xl border border-dashed border-border text-sm text-muted-foreground', className)} style={{ height }}>{emptyText}</div>;
	}

	const n = points.length;
	// A single point has no "before"/"after" to draw a line between, so
	// xAt alone collapses top and bottom to the same x - a zero-width path
	// that renders nothing. Give a single point a visible band instead of
	// vanishing under a caption that claims there's data.
	const bandHalf = 48;
	const xAt = (i) => (n === 1 ? W / 2 : padX + (i * (W - 2 * padX)) / (n - 1));
	const yAt = (v) => padTop + (H - padTop - padBottom) * (1 - (max ? v / max : 0));
	const pathFor = (layer) => {
		if (n === 1) {
			const { y0, y1 } = layer[0];
			const left = (W / 2 - bandHalf).toFixed(1), right = (W / 2 + bandHalf).toFixed(1);
			return `M${left},${yAt(y1).toFixed(1)} L${right},${yAt(y1).toFixed(1)} L${right},${yAt(y0).toFixed(1)} L${left},${yAt(y0).toFixed(1)} Z`;
		}
		const top = layer.map((s, i) => `${xAt(i).toFixed(1)},${yAt(s.y1).toFixed(1)}`);
		const bottom = layer.map((s, i) => `${xAt(i).toFixed(1)},${yAt(s.y0).toFixed(1)}`).reverse();
		return `M${top.join(' L')} L${bottom.join(' L')} Z`;
	};
	const onMove = (e) => {
		const rect = e.currentTarget.getBoundingClientRect();
		const rel = ((e.clientX - rect.left) / rect.width) * W;
		const idx = n === 1 ? 0 : Math.round(((rel - padX) / (W - 2 * padX)) * (n - 1));
		const clamped = Math.max(0, Math.min(n - 1, idx));
		if (clamped !== hoverIdx) { setHoverIdx(clamped); onHover?.(points[clamped], clamped); }
	};

	return (
		<svg viewBox={`0 0 ${W} ${H}`} className={cn('w-full', className)} style={{ height }} role="img" aria-label={ariaLabel} preserveAspectRatio="none"
			onMouseMove={onMove} onMouseLeave={() => { setHoverIdx(null); onHover?.(null, null); }}>
			{[0.25, 0.5, 0.75].map((f) => <line key={f} x1={padX} x2={W - padX} y1={yAt(max * f)} y2={yAt(max * f)} stroke="currentColor" className="text-border" strokeWidth="1" />)}
			{keys.map((k) => <path key={k.key} data-series={k.key} d={pathFor(layers[k.key])} fill={k.color} fillOpacity="0.85" stroke="none" />)}
			{hoverIdx !== null && <line x1={xAt(hoverIdx)} x2={xAt(hoverIdx)} y1={padTop} y2={H - padBottom} stroke="currentColor" className="text-foreground" strokeWidth="1.5" />}
			<line x1={padX} x2={W - padX} y1={H - padBottom} y2={H - padBottom} stroke="currentColor" className="text-muted-foreground" strokeWidth="1" />
		</svg>
	);
}
export default SeriesChart;
